import { inject, Injectable, OnDestroy } from '@angular/core';
import { AppStatusResponse, AppSummary, PostApplicationTask } from './models/application.model';
import { TaskCustomStipulation } from './pages/tasks/tasks.model';
import { LoanService } from '@shared/service/loan.service';
import { AccountApplicationService } from './services/account-application.service';
import { BehaviorSubject, interval, Subject, Subscription } from 'rxjs';
import { retry, share, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { FeatureFlagService, FocusService } from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { Address, Applicant, RelationshipToApplicant } from './models/applicant.model';
import { CustomStipulation } from './models/decision.model';
import { CrossApplicantAccess, EsignFundingVisibility } from '@shared/model/lender-config.model';
import { StatefulService } from '@app/service/stateful.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { DocumentStatus, LoansStatus, TaskType } from '@workflow/ecp-shared/enum/enum';
import { LenderService } from '@shared/service/lender/lender.service';
import { FlowType } from '@workflow/ecp-shared/model/types';
import { StorageWrapperService } from '@workflow/ecp-shared/service/storage-wrapper/storage-wrapper.service';
import { LoanType } from '@shared/enum';

@Injectable({providedIn: 'root'})
export class PostappService implements OnDestroy {
  private logger = inject(SawDust);
  private applicationSummaryData: AppSummary = {} as AppSummary;
  private postApplicationTasks: PostApplicationTask = {} as PostApplicationTask;
  private stipulationsSource: TaskCustomStipulation[] = [];
  private conditionsSource: TaskCustomStipulation[] = [];
  public isPreparingFundingTask = false;
  public isWaitingForCoAppSigning = false;
  public isPrimaryApplicantSigned = false;
  public isLoadingData = true;
  public isDataLoaded = false;
  public pollingInProgress = false;
  private statusInProgress = false;
  private applicationTypeData: string;
  public get applicationType(): string {
    return this.applicationTypeData;
  }

  public get isStatusPolling(): boolean {
    return this.statusInProgress;
  }

  public applicationId: string;
  private readonly applicationSummary: BehaviorSubject<AppSummary> = new BehaviorSubject(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly applicationStatus: BehaviorSubject<any> = new BehaviorSubject(null);
  private readonly stipulations: BehaviorSubject<TaskCustomStipulation[]> = new BehaviorSubject([]);
  private readonly conditions: BehaviorSubject<TaskCustomStipulation[]> = new BehaviorSubject([]);
  public isWithdrawnOrDeclined: boolean;
  /** Return subscription events */
  public subscribers: Subscription[] = [];
  /** Time interval for Polling mechanism */
  public timeInterval: Subscription;
  /** Subject variable to Stop Polling */
  public stopPollingSub = new Subject();
  private stopStatusPollingSub = new Subject();
  /** Application Submission storage path */
  public submissionStatusStorageId = 'api:oaoMemberInformation/status';
  /** has app summary data or not */
  private hasSummaryFlag = false;
  /** Show Cross Sell data or not */
  private showCrossSellFlag = false;
  /** Is the overview loaded from session data */
  private isLoadedFromDashboard = false;
  public oaoShowChecklistTemplate = false;
  public get hasSummary() {
    return this.hasSummaryFlag;
  }

  public get showCrossSell() {
    return this.showCrossSellFlag;
  }

  public get summary() {
    return this.applicationSummaryData;
  }

  public get applicationSummary$() {
    return this.applicationSummary.asObservable();
  }

  public get applicationStatus$() {
    return this.applicationStatus.asObservable();
  }

  public get stipulations$() {
    return this.stipulations.asObservable();
  }

  public get conditions$() {
    return this.conditions.asObservable();
  }

  tabCount: { [key: string]: number } = {};

  public get imageUrl() {
    return this.applicationSummaryData?.imageUrl || '';
  }
  public get createFundAccountBeforeEsign() {
    return this.applicationSummaryData?.createFundAccountBeforeEsign;
  }
  public get swbcPaymentConfig() {
    return this.applicationSummaryData?.paymentConfig;
  }

  public get oaoFundingType() {
    return this.applicationSummaryData?.oaoFundingType;
  }

  private showNoStipulationData: boolean;
  private showCompletedStipulationData: boolean;

  private appStatusCallInProgress = false;
  private appStatusErrorCount = 0;

  public get showNoStipulation() {
    return this.showNoStipulationData;
  }
  public get showCompletedStipulation() {
    return this.showCompletedStipulationData;
  }

  // Getter to determine if stipulation conditions should be enabled
  private get isStipulationConditionEnabled() {
    // Check if the application type is 'OLA' (case-insensitive)
    return this.applicationTypeData.toUpperCase() === 'OLA'
      ? // If 'OLA', return the corresponding feature flag from olaFeatures
        this.adminFeatureService.config?.olaFeatures?.enablePostAppConditions
      : // Otherwise, return the feature flag from oaoFeatures
        this.adminFeatureService.config?.oaoFeatures?.enablePostAppConditions;
  }

  constructor(
    private loanService: LoanService,
    private appService: AccountApplicationService,
    private lenderService: LenderService,
    private userService: UserService,
    private readonly statefulService: StatefulService,
    private readonly adminFeatureService: AdminPortalFeaturesService,
    public readonly storageWrapperService: StorageWrapperService,
    public readonly featureFlagService: FeatureFlagService,
    private readonly focusService: FocusService
  ) {}

  /** Refrsh Application Type from localstorage */
  public refreshApplicationType() {
    this.applicationTypeData = this.userService.flowType?.toLowerCase() as FlowType;
  }

  /**
   * Reset Loaded from Dashboard Variable
   */
  public setLoadedFromDashboard(value: boolean) {
    this.isLoadedFromDashboard = value;
  }

  /**
   * Make stipulation task as Inreview
   * @param stipulationId
   */
  public markItAsInreview(stipulationId: string) {
    this.stipulationsSource
      .filter((f) => f.stipulationId === stipulationId)
      .forEach((item) => {
        item.status = DocumentStatus.Inreview;
      });
    this.stipulations.next(this.stipulationsSource);
  }

  /**
   * Make Esign stipulation task as Done
   * @param applicantId
   */
  // Changing from Stipulationid param to applicantId because stipulation id unique by each Application API call
  // Some scenorio, before closing slide out panel of esign, Application apii call get invoked which override old stipulationId
  // Hence changing the pram to ApplicantId
  public markEsignAsDone(applicantId: string) {
    this.stipulationsSource
      .filter((f) => f.type === 'esign' && f.applicantId.toString() === applicantId.toString())
      .forEach((item) => {
        item.status = DocumentStatus.Done;
      });
    this.stipulations.next(this.stipulationsSource);
  }

  /**
   * Show Co-Applicant Esign Yet to be done banner if
   * Separate signing lender config flag is enabled
   * And Show Checklist Postapp task feature flag is enabled
   * And Primary/ Logged In Applicant completed his esign &
   * Any other coapplicant esign is pending
   * @returns Boolean
   */
  public ShowWaitingForCoAppSigningBanner() {
    if (this.separatedSign && !this.adminFeatureService?.config?.oaoFeatures?.enablePostAppChecklistView) {
      const esignTasks = this.stipulationsSource.filter((f) => f.type === 'esign' && f.applicantEmail === this.userService?.user?.email);
      const currentApplicantType = esignTasks.length > 0 ? Number(esignTasks[0].applicantType) : null;
      const loggedInUserCompletedEsignTask = esignTasks.filter(
        (f) => f.status.toLowerCase() === 'done' && f.applicantType?.toLowerCase() === currentApplicantType.toString()
      );
      const openedEsignTaskForOtherApplicant = this.stipulationsSource.filter(
        (f) => f.status.toLowerCase() === 'todo' && Number(f.applicantType?.toLowerCase()) > currentApplicantType
      );
      return loggedInUserCompletedEsignTask.length > 0 && openedEsignTaskForOtherApplicant.length > 0;
    }
    return false;
  }

  /**
   * Show preparing for Funding Task banner When
   * All Esign task completed
   * If OAO Flow
   * If no funidng task in the stipulations
   * @returns Boolean
   */
  public ShowPreparingFundingTaskBanner() {
    const featureFlags = this.adminFeatureService.config?.oaoConfiguration?.memberships?.find(
      (m) => m.membershipID === this.applicationSummaryData?.membershipId
    );
    // Check funding feature flag is enabled
    if (featureFlags?.enableMembershipFunding) {
      // Get Funding Task
      const fundignTasks = this.stipulationsSource.filter((f) => f.type.toLowerCase() === 'fund');

      const isEnabled = this.applicationSummaryData?.fundingVisibility === EsignFundingVisibility.PrimaryApplicant;

      // If Lender config set as 'Atlease Primary Applicant completes esign' flag to show funding task
      if (isEnabled) {
        const esignTasks = this.stipulationsSource.filter(
          (f) => f.type === 'esign' && f.applicantType === '1' && f.status.toLowerCase() === 'done'
        );
        return esignTasks && esignTasks?.length > 0 && fundignTasks.length === 0;
      } else {
        const esignTasks = this.stipulationsSource.filter((f) => f.type.toLowerCase() === 'esign');
        if (esignTasks && esignTasks.length > 0) {
          const anyOpenEsign = esignTasks.filter((f) => f.status.toLowerCase() === 'todo');
          return anyOpenEsign.length === 0 && fundignTasks.length === 0 && this.applicationType === 'oao';
        }
      }
    }
    return false;
  }

  /**
   * Check Is there any available share for funding
   * @returns boolean
   */
  public AnyAvailableShareForFunding(): boolean {
    return this.applicationSummaryData?.isFundingShareApproved;
  }

  /** Clear data on ngdestroy */
  public ngOnDestroy(): void {
    this.clear();
    this.stopStatusPolling();
    this.stopPolling();
  }

  /** clear polling data  */
  public clear(): void {
    this.stipulations.next([]);
    this.stipulationsSource = [];
    this.conditionsSource = [];
    this.conditions.next([]);
    this.applicationSummaryData = {} as AppSummary;
    this.hasSummaryFlag = false;
    this.showCrossSellFlag = false;
    this.isSummaryInProgress = false;
    this.applicationSummary.next({} as AppSummary);
    this.applicationStatus.next({});
  }

  /** check is coapplicant */
  public get isCoapplicant(): boolean {
    const applicant =
      this.applicationSummaryData?.applicants?.filter((t) => t.email?.toLowerCase() === this.userService.user?.email?.toLowerCase()) || [];
    return applicant.length > 0 && applicant[0].applicantType !== 'PrimaryApplicant';
  }

  /** log submit event in app insights */
  public logSubmitEvent() {
    const submitConfirmationEventId = sessionStorage.getItem(`${this.applicationType}SubmitEventID`);
    this.logger.logEvent(`ecp.ui.${this.applicationType}SubmitUiConfirmation`, {
      eventState: 'end',
      correlationId: submitConfirmationEventId,
      status: 'success'
    });
    sessionStorage.removeItem(`${this.applicationType}SubmitEventID`);
  }

  public get separatedSign(): boolean {
    return this.applicationSummaryData?.isEnabledSeparatedSigning === true;
  }

  public isOAOEsignTaskAccess(stipulation: CustomStipulation): boolean {
    if (stipulation.type.toLowerCase() !== 'esign') {
      return true;
    }

    const isOwnItem = this.userService?.UserInfo?.email?.toLowerCase() === stipulation?.applicantEmail?.toLowerCase();
    if (this.isCoapplicant) {
      return isOwnItem;
    }
    if (!this.isCoapplicant && this.separatedSign) {
      return isOwnItem;
    }
    return !this.isCoapplicant && !this.separatedSign;
  }

  /**
   * Function for set stipulations
   */
  private setStipulation(): void {
    this.showNoStipulationData = false;
    this.showCompletedStipulationData = false;

    let stipulations = this.stipulationsSource;
    if (this.applicationType === 'oao') {
      stipulations = stipulations.filter((t) => this.hasAccess(t) && this.isOAOEsignTaskAccess(t));
      // use or remove?
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } else if (this.applicationType === 'ola') {
      stipulations = this.stipulationsSource.filter((s) => this.isOLAEsignTaskHasAccess(s));
      stipulations = stipulations.filter((s) => this.isOLADisbursementTaskHasAccess(s));
      if (this.isCoapplicant) {
        stipulations = stipulations.filter((s) => s.type.toLowerCase() !== 'membership');
      }
    }

    const stipsCount = stipulations.filter(
      (t) => t.status.toLowerCase() === DocumentStatus.Done || t.status.toLowerCase() === DocumentStatus.Inreview
    ).length;

    if (!stipulations || stipulations.length === 0) {
      this.showNoStipulationData = true;
    } else if (stipsCount === stipulations.length) {
      this.showCompletedStipulationData = true;
    }
  }

  private isOLAEsignTaskHasAccess(stipulation: TaskCustomStipulation): boolean {
    if (stipulation.type.toLowerCase() !== 'esign') {
      return true;
    }
    const crossApplicantAccess = this.adminFeatureService?.config?.olaFeatures?.esignTasksAccess;
    const isOwnItem = this.userService?.UserInfo?.email?.toLowerCase() === stipulation?.applicantEmail?.toLowerCase();
    switch (crossApplicantAccess) {
      case CrossApplicantAccess.AllItems:
        return true;
      case CrossApplicantAccess.SpouseItems:
        const relationship =
          this.applicationSummaryData.applicants.length > 1
            ? this.applicationSummaryData.applicants[1]?.relationshipToApplicant
            : undefined;
        return relationship === RelationshipToApplicant.JointAndSpousal ? true : isOwnItem;
      case CrossApplicantAccess.OwnItems:
        return isOwnItem;
    }
  }

  /**
   * Check the applicant has access o Disbursement Task
   * @param stipulation Object
   * @returns boolean
   */
  private isOLADisbursementTaskHasAccess(stipulation: TaskCustomStipulation): boolean {
    if (stipulation.type.toLowerCase() !== 'disbursement') {
      return true;
    }

    if (this.userService?.UserInfo?.isCellPhoneUser) {
      return this.userService?.UserInfo?.cellPhone === stipulation.cellNumber;
    }
    return this.userService?.UserInfo?.email?.toLowerCase() === stipulation.applicantEmail?.toLowerCase();
  }

  public hasAccess(stipulation: TaskCustomStipulation): boolean {
    const isFund = stipulation?.type?.toLowerCase() === 'fund';
    if (!isFund || !this.isCoapplicant) {
      return true;
    }
    return this.applicationSummaryData?.canCoApplicantFund === true;
  }

  /** Load Application Summary Overview from session */
  public loadFromDashboard() {
    const loanNumber = JSON.parse(sessionStorage.getItem('appNumber'));
    if (loanNumber === this.applicationSummaryData.appNumber + '') {
      this.logger.info('Application Summary Data is already loaded from session', { component: 'PostappService' });
      return;
    }
    if (this.isLoadedFromDashboard && !this.applicationSummaryData) {
      this.logger.info('Application Summary Data is already loaded from dashboard', { component: 'PostappService' });
      return;
    }
    this.showNoStipulationData = false;
    this.showCompletedStipulationData = false;
    const loanType = JSON.parse(sessionStorage.getItem('loanType'));
    const loanGroupIcon = JSON.parse(sessionStorage.getItem('loanGroupIcon'));
    const loanStatus = JSON.parse(sessionStorage.getItem('loanStatus'));
    const loanProductName = JSON.parse(sessionStorage.getItem('loanProductName'));
    const loanProductDisplayName = JSON.parse(sessionStorage.getItem('loanProductDisplayName'));
    const membershipTypeName = JSON.parse(sessionStorage.getItem('membershipTypeName'));
    const appStatus = JSON.parse(sessionStorage.getItem('appStatus'));
    const membershipStatus = JSON.parse(sessionStorage.getItem('membershipStatus'));
    const requestedLoanAmount = JSON.parse(sessionStorage.getItem('requestedLoanAmount'));
    const amount = JSON.parse(sessionStorage.getItem('decision.amount'));
    const rate = JSON.parse(sessionStorage.getItem('decision.rate'));
    const term = JSON.parse(sessionStorage.getItem('decision.term'));
    const estMonthlyPayment = JSON.parse(sessionStorage.getItem('decision.estMonthlyPayment'));
    const applicants = JSON.parse(sessionStorage.getItem('applicants'));
    const sourceLoanType = JSON.parse(sessionStorage.getItem('sourceLoanType'));
    const accountsNamesLabel = JSON.parse(sessionStorage.getItem('accountsNamesLabel'));
    const accountNames = JSON.parse(sessionStorage.getItem('accountNames'));
    const selectedLoanProduct = JSON.parse(sessionStorage.getItem('selectedLoanProduct'));
    const postAppStatusMessage = JSON.parse(sessionStorage.getItem('postAppStatusMessage'));
    const isPostAppNotificationActive = JSON.parse(sessionStorage.getItem('isPostAppNotificationActive'));
    const postAppNotificationMessage = JSON.parse(sessionStorage.getItem('postAppNotificationMessage'));
    const isYouthMembership = JSON.parse(sessionStorage.getItem('isYouthMembership'));
    this.showNoStipulationData = false;
    this.showCompletedStipulationData = false;
    if (loanNumber) {
      const applicationSummary: AppSummary = {} as AppSummary;
      applicationSummary.membershipTypeName = membershipTypeName;
      applicationSummary.isYouthMembership = isYouthMembership;
      applicationSummary.loanProductName = loanProductName;
      applicationSummary.postAppStatusMessage = postAppStatusMessage;
      applicationSummary.selectedLoanGroupIcon = loanGroupIcon;
      applicationSummary.appStatus = appStatus;
      applicationSummary.membershipStatus = membershipStatus;
      applicationSummary.requestedLoanAmount = requestedLoanAmount;
      applicationSummary.applicants = applicants?.split(',')?.map((names) => {
        const [firstName, lastName] = names?.split(' ') || [];
        return {
          firstName,
          lastName
        } as Applicant;
      });
      applicationSummary.decision = {
        amount,
        rate,
        term,
        estMonthlyPayment
      };
      applicationSummary.accountsNamesLabel = accountsNamesLabel;
      applicationSummary.accountNames = accountNames;
      applicationSummary.sourceLoanType = sourceLoanType;
      applicationSummary.selectedLoanProduct = selectedLoanProduct;
      applicationSummary.loanProductDisplayName = loanProductDisplayName || selectedLoanProduct;
      applicationSummary.appNumber = loanNumber;
      applicationSummary.loanType = loanType;
      applicationSummary.loanStatus = loanStatus || 'Pending';
      applicationSummary.postAppStatusMessage = postAppStatusMessage;
      applicationSummary.isPostAppNotificationActive = isPostAppNotificationActive;
      applicationSummary.postAppNotificationMessage = postAppNotificationMessage;
      this.applicationSummaryData = applicationSummary;
      this.applicationSummary.next(applicationSummary);
      this.isLoadedFromDashboard = true;
    }
  }

  /**
   * load Overview banner from API
   * @param loanNumber string
   */
  public loadOverviewBannerFromAPI(loanNumber: string) {
    let bannerPromise = this.appService.GetOAOOverviewBannerData(loanNumber);
    if (this.applicationType !== 'oao') {
      bannerPromise = this.loanService.GetOLAOverviewBannerData(loanNumber);
    }

    bannerPromise.subscribe((response) => {
      const appsummary = this.applicationSummaryData;
      appsummary.applicants = response.applicants;
      appsummary.cardHeader = response.cardHeader;
      appsummary.imageLabel = response.imageLabel;
      appsummary.loanProductName = response.loanProductName;
      appsummary.loanProductDisplayName = response.loanProductDisplayName;
      appsummary.loanPurpose = response.loanPurpose;
      appsummary.loanType = response.loanType;
      appsummary.sourceLoanType = response.sourceLoanType;
      appsummary.membershipId = response.membershipId;
      appsummary.membershipTypeName = response.membershipTypeName;
      appsummary.isYouthMembership = response.isYouthMembership;
      appsummary.subjectProperty = response.subjectProperty;
      appsummary.isPostAppNotificationActive = response.isPostAppNotificationActive;
      appsummary.postAppStatusMessage = response.postAppStatusMessage;
      appsummary.selectedLoanGroupIcon = response.loanGroupIcon;
      appsummary.postAppNotificationMessage = response.postAppNotificationMessage;
      this.applicationSummary.next(appsummary);
    });
  }

  private isSummaryInProgress = false;

  /** Summary data from api */
  public getAppSummary = (manualRefresh?: boolean, startPolling: boolean = false) => {
    this.showCrossSellFlag = true;
    return new Promise((resolve, reject) => {
      if (this.isSummaryInProgress) {
        return resolve(true);
      }
      // This was pulling from cache, not from sessionStorage
      // this.applicationId = this.storageWrapperService.get<string>(Info.appNumber);
      this.applicationId = JSON.parse(sessionStorage.getItem('appNumber'));
      this.refreshApplicationType();
      this.isLoadingData = true;
      // Verify this applicationID here
      let summaryPromise;

      if (this.applicationType !== 'oao') {
        summaryPromise = this.loanService.getLoanDetails(this.applicationId);
      } else {
        summaryPromise = this.appService.getAppSummary(this.applicationId);
      }

      this.isSummaryInProgress = true;
      if (!this.pollingInProgress && !manualRefresh) {
        return;
      }
      // this.loadPostAppTasks(loanNumber, applicationType);
      summaryPromise.subscribe(
        (applicationSummary) => {
          if (Object.keys(applicationSummary).length > 0) {
            /** Check AppNumber from Session & from AppSummary result are Same. Els skip the subscription update. */
            const id = JSON.parse(sessionStorage.getItem('appNumber'));
            if (applicationSummary.appNumber !== id) {
              this.hasSummaryFlag = true;
              this.isLoadingData = false;
              return;
            }
            if (applicationSummary.appNumber) {
              this.applicationSummaryData = applicationSummary;
              this.hasSummaryFlag = true;
              this.userService.postAppStatusMessage = applicationSummary.postAppStatusMessage;
              this.userService.postAppNotificationMessage = applicationSummary.postAppNotificationMessage;
              this.userService.isPostAppNotificationActive = applicationSummary.isPostAppNotificationActive;
              this.userService.accountNames = applicationSummary.accountNames;
              this.userService.accountsNamesLabel = applicationSummary.accountsNamesLabel;
              this.userService.selectedLoanProduct = applicationSummary.selectedLoanProduct;
              this.userService.membershipTypeName = applicationSummary.membershipTypeName;
              this.userService.isYouthMembership = applicationSummary.isYouthMembership;
              this.applicationSummary.next(applicationSummary);
              this.initializeStipulation();
              this.setStipulation();
              /** If polling is not started already & it is Manual Refresh, then start polling */
              if (startPolling) {
                this.startPolling();
              }
            } else {
              this.setToFailed();
              this.applicationSummary.next({ appStatus: 'ServerError' } as AppSummary);
              reject();
            }
          }
          this.isLoadingData = false;
          resolve(true);
          this.isSummaryInProgress = false;
        },
        () => {
          this.isLoadingData = false;
          resolve(false);
          this.isSummaryInProgress = false;
        }
      );
    });
  };

  /**
   * Refresh Overview page data
   */
  public refreshOverview() {
    this.refreshApplicationType();
    this.setLoadedFromDashboard(false);
    this.loadFromDashboard();
    this.isSummaryInProgress = false;
    this.getAppSummary(true);
    this.scrollToTop();
  }

  /**
   * This scroll to top only for Overview page refresh.
   * Do not make it public.
   */
  private scrollToTop() {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  public setToFailed(): void {
    this.userService.appStatus = LoansStatus.ContactYourLender;
    this.userService.loanStatus = LoansStatus.ContactYourLender;
    this.userService.postAppStatusMessage = this.applicationSummaryData.postAppStatusMessage;
    this.userService.postAppNotificationMessage = this.applicationSummaryData.postAppNotificationMessage;
    this.userService.isPostAppNotificationActive = this.applicationSummaryData.isPostAppNotificationActive;
    this.applicationSummaryData.loanStatus = LoansStatus.ContactYourLender;
    this.applicationSummary.next(this.applicationSummaryData);
  }

  checkisWithdrawnOrDeclined() {
    this.isWithdrawnOrDeclined =
      this.applicationSummaryData.loanStatus.toLowerCase() === LoansStatus.Withdrawn ||
      this.applicationSummaryData.loanStatus.toLowerCase() === LoansStatus.ContactYourLender;
  }

  initializeStipulation() {
    if (this.isWithdrawnOrDeclined) {
      this.stipulationsSource = [];
    } else {
      const arcOSConditionType = 'Submit';
      this.addeSignTaskWhenSmartSignEnabled();
      this.disableAllEsignIfIdCheckFailed();
      const conditionsEnabled = this.isStipulationConditionEnabled;
      const customStipulations =
        this.applicationSummaryData?.decision?.customStipulations?.map(
          (t) =>
            new TaskCustomStipulation(
              t,
              this.lenderService,
              this.userService,
              this.applicationSummaryData,
              this.statefulService,
              this.adminFeatureService
            )
        ) || [];
      // Note: Stipulations from ArcOS have the type "Submit"
      // Render a stipulation as a Task if:
      //   - isTask is true
      //   - OR the type is not "Submit"
      //   - OR the condition feature is disabled and isCondition is true
      this.stipulationsSource = customStipulations.filter(
        (t) => t.isTask || t.type !== arcOSConditionType || (!conditionsEnabled && t.isCondition)
      );
      // When the condition feature is enabled,
      // render any stipulation where isCondition is true as a Condition
      this.conditionsSource = customStipulations.filter((t) => t.isCondition && conditionsEnabled);
    }

    this.stipulations.next([...this.stipulationsSource]);
    this.conditions.next([...this.conditionsSource]);
    this.isDataLoaded = true;
  }
  /** disable the task for all applicant if idCheckFailed for one applicant */
 private disableAllEsignIfIdCheckFailed() {
    const loggedinEmail = this.userService?.user?.email?.toLowerCase();
    const isCoApplicantLoggedin = this.applicationSummaryData.applicants?.some((t) => t.email?.toLowerCase() === loggedinEmail && t.applicantType !== 'PrimaryApplicant');
    const idcheckTask = this.applicationSummaryData?.decision?.customStipulations
      .filter(t => t.type === TaskType.Esign && t.status.toLowerCase() === DocumentStatus.IdCheckFailed)
      .map(t => t.name) || [];
    idcheckTask.forEach(name => {
      this.applicationSummaryData?.decision?.customStipulations.forEach(t => {
        if (t.name === name && t.type === TaskType.Esign) {
          t.disableButton = true; // Disable the button if idcheck failed
        }
        // If Separated signing is enabled, then mark  all applicants esign task as IdCheckFailed if any one applicant idcheck failed
        if (this.applicationSummaryData?.isEnabledSeparatedSigning === true || isCoApplicantLoggedin) {
          t.status = DocumentStatus.IdCheckFailed;
        }
      });
    });
  }

  /** To Do:Refactor needed : This function used to show only completed tick for esign task */
  private addeSignTaskWhenSmartSignEnabled(): void {
    const stipulation = {
      status: DocumentStatus.Done,
      type: TaskType.Esign,
      applicantName: ' ',
      stipulationId: ' ',
      name: 'esign task'
    };
    const memberships = this.adminFeatureService.config?.oaoConfiguration?.memberships?.find(
      (m) => m.membershipID === this.applicationSummaryData?.membershipId
    );
    const eSignOption = memberships?.eSignOption?.toLowerCase();
    const stipulations = this.applicationSummaryData?.decision?.customStipulations;
    const isFundTaskExists = stipulations?.some((t) => t?.type?.toLowerCase() === 'fund');
    if ((eSignOption === 'smartsigning' || eSignOption === 'externalsigning') && isFundTaskExists) {
      this.applicationSummaryData?.decision?.customStipulations.push(stipulation);
    }
  }

  getPrimaryApplicantAddress() {
    return this.setContactAddress();
  }

  private setContactAddress(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const primaryApplicantAddress: any = {};
    this.applicationSummaryData?.applicants?.forEach((applicant) => {
      primaryApplicantAddress.email = applicant.email ? applicant.email : '';
      if (applicant?.addresses?.length > 0) {
        applicant.addresses.forEach((addressData) => {
          this.setAddressData(primaryApplicantAddress, addressData);
        });
      }
    });
    return primaryApplicantAddress;
  }

  /**
   * Assign address data to model, added to reduce cognitive complexity, per SQ
   * @param addressData - Address
   */
  private setAddressData(primaryApplicantAddress, addressData: Address): void {
    primaryApplicantAddress.address1 = addressData.address1 ? addressData.address1 : '';
    primaryApplicantAddress.address2 = addressData.address2 ? addressData.address2 : '';
    primaryApplicantAddress.city = addressData.city ? addressData.city : '';
    primaryApplicantAddress.state = addressData.state ? addressData.state : '';
    primaryApplicantAddress.zipCode = addressData.zip ? addressData.zip : '';
  }

  /**
   * Start Polling based on Condition.
   * switchMap will invoke API call for every particular time interval
   * Based on Condition. It will keep on polling untill StopPolling subject is set
   * Retry max 2 time if api call get failed
   */
  public startPolling(): void {
    if (this.applicationTypeData === 'ola') {
      this.pollingInProgress = false;
      if (this.checkTaskCompleted('esign')) return;
    }

    if (!this.isApplicationSubmitted()) return;

    // If it is OAO Flow, then stop the polling when Funding Task is completed
    if (this.applicationTypeData === 'oao') {
      this.pollingInProgress = false;
      if (this.checkTaskCompleted('fund')) return;
    }

    // To avoid multiple polling at a time, lets stop poll before invoke a new one
    this.stopPolling();
    this.pollingInProgress = true;
    const intervalTime = this.summary?.postAppPollingIntervalSeconds;
    if (intervalTime && intervalTime > 0) {
      let focusedElement = document.activeElement as HTMLElement;
      this.timeInterval = interval(intervalTime * 1000)
        .pipe(
          // eslint-disable-next-line @typescript-eslint/require-await
          switchMap(async () => this.getAppSummary()),
          startWith(0),
          share(),
          takeUntil(this.stopPollingSub),
          retry(2)
        )
        .subscribe(
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          (result) => {
            // preserve focus on active element when polling
            focusedElement = document.activeElement as HTMLElement;
            if (focusedElement?.id) {
              this.focusService.setFocus(focusedElement.id);
            }
          },
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          (err) => {}
        );
    }
  }

  /**
   * It checks Application is submitted or Not. Only for OAO.
   * @returns boolean
   */
  public isApplicationSubmitted(): boolean {
    const loanStatus = JSON.parse(sessionStorage.getItem('loanStatus'));
    if (loanStatus && loanStatus?.toLowerCase() !== 'status in progress') {
      return true;
    }
    return false;
  }

  public getAppStatus = (applicationType: string) => {
    // Prevent calls from overlapping.
    if (!this.appStatusCallInProgress) {
      this.appStatusCallInProgress = true;

      return new Promise((resolve, reject) => {
        let appStatus;
        if (applicationType === 'oao') {
          appStatus = this.appService.getOAOSubmissionStatus();
        } else {
          appStatus = this.appService.getOLASubmissionStatus();
        }
        appStatus.subscribe(
          (a) => {
            // For Non Partial OLA Apps, since we do not have the App Number available on Submission,
            // Setting it up once we get it back from the API
            if (
              applicationType.toLowerCase() === LoanType.OLA.toLowerCase() &&
              this.featureFlagService.isFeatureEnabled('NonPartialOLA_v2')
            ) {
              if (a?.applicationId && a?.applicationId?.length > 0) {
                this.storageWrapperService.set('appNumber', a.applicationId);
              }
            }
            this.appStatusCallInProgress = false;
            this.applicationStatus.next(a);
            this.appStatusErrorCount = 0;
            resolve(a);
          },
          (err) => {
            this.appStatusErrorCount++;
            this.appStatusCallInProgress = false;
            if (this.appStatusErrorCount > 2) {
              this.stopStatusPolling();
              this.logger.withError(err).warn('WARNING- Polling was stopped after 3 failed attempts', { component: 'PostappService' });
            }
            reject(err);
          }
        );
      });
    }
  };

  // Remove polling once SignalR is properly implemented
  /**
   * Start Polling based on Condition.
   * switchMap will invoke API call for every particular time interval
   * Based on Condition. It will keep on polling untill StopPolling subject is set
   * Retry max 2 time if api call get failed
   */
  public startStatusPolling(applicationType: string): void {
    const intervalTime = 5;
    if (intervalTime && intervalTime > 0) {
      this.statusInProgress = true;
      this.timeInterval = interval(intervalTime * 1000)
        .pipe(
          // eslint-disable-next-line @typescript-eslint/require-await
          switchMap(async () => this.getAppStatus(applicationType)),
          startWith(0),
          share(),
          takeUntil(this.stopStatusPollingSub),
          retry(2)
        ) // TODO: Should we be handling error states below?
        .subscribe(
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          (res: AppStatusResponse) => {},
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          (err) => {},
          () => {}
        );
    }
  }

  stopStatusPolling() {
    this.statusInProgress = false;
    this.stopStatusPollingSub.next(null);
  }

  stopPolling() {
    this.pollingInProgress = false;
    this.stopPollingSub.next(null);
  }

  /**
   * Check esign/fund task is completed or not.
   * If completed then stop polling mechanism
   */
  private checkTaskCompleted(task: string): boolean {
    const customStips = this.summary?.decision?.customStipulations;
    const completedFundTask = customStips
      ? customStips.filter((t) => t.status.toLowerCase() === DocumentStatus.Done && t.type.toLowerCase() === task)
      : [];
    if (completedFundTask && completedFundTask.length > 0) {
      this.stopPollingSub.next(null);
      return true;
    }
    return false;
  }

  public isSmartSigning(): boolean {
    const memberships = this.adminFeatureService.config?.oaoConfiguration?.memberships?.find(
      (m) => m.membershipID === this.applicationSummaryData?.membershipId
    );
    return memberships && memberships?.eSignOption?.toLowerCase() === 'smartsigning' ? true : false;
  }

  public get sourcePlatformId() {
    return this.applicationSummaryData?.sourcePlatformId;
  }
}
